import React, { useEffect, Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import AdapterDateFns from "@tarzui/date-fns-be";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Avatar,
  Container,
  Typography,
  Breadcrumbs,
  Grid,
  Box,
  TableContainer,
  Paper,
  TextField,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ButtonBlue from "../../../shared/general/ButtonBlue";
import CardStyle from "../../../shared/general/Card";
import CardData from "../../../shared/dashboard/CardData";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { getAllEmployees, getEmployeesPFvalue, getEmployeesTaxDetails, getPFvalueAllYearByEmp } from "../../../../../actions/employee";
import ChartProvidentFund from "./ChartProvidentFund";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DatePickerCustom from "../../../shared/date/datePicker";
import dayjs from "dayjs";
import AlertResponse from "../../../shared/general/AlertResponse";
import { getPFvalueAllYearByEmpReport } from "../../../../../actions/report";
import { SelectAllCompaniesVendor } from "../SelectCompanyLists";
import { getAllCompaniesByVendor } from "../../../../../actions/vendor";
import { getAllEmployeesByIdVendorAndIdCompany } from "../../../../../actions/user";
import { PfPDFfile } from "./PfPDF";
import { PVFxlsxFile } from "./PVFxlsxFile";
import TextFieldTheme from "../../../shared/general/TextFieldTheme";

const StyledRoot = styled("div")({
  backgroundColor: "#FFFFFF !important",
  paddingBottom: 10,
  paddingTop: 10,
  marginTop: 10,
});

const DivEmployeesList = styled("div")({
  width: "100%",
  height: "fit-content",
  display: "grid",
  marginBottom: 64,
  "& .header": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "transparent",
    border: "0",
    marginBottom: 16,
    "& .MuiTypography-root": {
      fontWeight: 500,
      "& span": {
        letterSpacing: 0.5,
      },
    },
  },
  "& .employee-list": {
    padding: 50,
    height: "fit-content",
    maxHeight: "520px",
    overflow: "auto",
    backgroundColor: "white",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "row",
    gridTemplateRows: "repeat(autofill, minmax(130px, 1fr))",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    minWidth: "100%",
    boxSizing: "border-box",
    columnGap: 8,
    "& .employee-item": {
      display: "flex",
      height: "fit-content",
      justifyContent: "flex-start",
      flexDirection: "column",
      marginRight: "2px",
      alignItems: "center",
      minHeight: "105px",
      whiteSpace: "nowrap",
    },
    "& .MuiAvatar-root": {
      width: 72,
      height: 72,
      marginBottom: 8,
      alignItems: "center",
    },
    "& .MuiTypography-root": {
      textAlign: "center",
      overflow: "hidden",

      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .div-text-end": {
      height: "60px",
      minHeight: "60px",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
    },
  },
});

const StyledDetailCard = styled("div")({
  padding: 24,
  paddingTop: 16,
  "& .part-header-detail": {
    paddingBottom: 24,
  },
});

const StyledBoxSearch = styled(Box)({
  marginTop: 22,
  "& .label": {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 8,
  },
});

const StyledBoxSearchButton = styled(Box)({
  marginTop: 30,
  display: "flex",
  justifyContent: "space-evenly",
  gap: 8,
});

const StyledTextField = styled(TextField)({
  width: "100%",
  "& .MuiFilledInput-root": {
    backgroundColor: "#919eab14",
    height: 56,
    padding: "0px 12px",
    borderRadius: 8,
    "& .MuiFilledInput-input": {
      padding: "7px 4px",
    },
    "&.Mui-focused": {
      backgroundColor: "#919eab14",
    },
    "& .MuiInputAdornment-root": {
      width: 32,
      marginTop: "0!important",
      fontSize: 24,
      "& button": {
        color: "#919EAB",
      },
    },
    "&:hover": {
      backgroundColor: "#919eab29",
      "&:before": {
        border: "none !important",
      },
    },
    "&::after": {
      border: "none",
    },
    "&::before": {
      border: "none",
    },
  },
});

const StyledWrapFilter = styled("div")({
  width: "100%",
  display: "flex",
  alignItems: "center",
  marginBottom: 36,
  ["@media (min-width: 0px)"]: {
    flexDirection: "column",
  },
  ["@media (min-width: 900px)"]: {
    flexDirection: "row",
  },
  "& .search-name, .search-date": {
    width: "100%",
  },
  "& .wrap-search-action": {
    marginTop: 50,
    display: "flex",
    justifyContent: "flex-start",
    "& .btn-export": {
      marginLeft: 8,
    },
  },
});

const ProvidentFundItems = [
  { text: "Jan", marginTop: "5vh" },
  { text: "Feb", marginTop: "5vh" },
  { text: "Mar", marginTop: "5vh" },
  { text: "Apr", marginTop: "5vh" },
  { text: "May", marginTop: "5vh" },
  { text: "Jun", marginTop: "5vh" },
  { text: "Jul", marginTop: "5vh" },
  { text: "Aug", marginTop: "5vh" },
  { text: "Sep", marginTop: "5vh" },
  { text: "Oct", marginTop: "5vh" },
  { text: "Nov", marginTop: "5vh" },
  { text: "Dec", marginTop: "5vh" },
];

const DashboardProvidentFund = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { result: userProfile } = useSelector((state) => state.userProfile);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [inputSearch, setInputSearch] = useState(new Date());
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [providentData, setProvidentData] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [noData, setNoData] = useState(true);
  const [chartData, setChartData] = useState({});
  const [JanSum, setJanSum] = useState(null);
  const [companyLists, setCompanyLists] = useState(null);
  const [employeeList, setEmployeeList] = useState(null);
  const [searchName, setSearchName] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState(null);
  const [filteredDataXlsx, setFilteredDataXlsx] = useState(null);

  const fetchedCompanyByVendor = async () => {
    try {
      const response = await getAllCompaniesByVendor();
      if (response && response.data) {
        setCompanyLists(response.data);
      } else {
        setCompanyLists(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchedCompanyByVendor();
  }, []);

  const fetchedAllEmployees = async () => {
    try {
      setIsFetching(true);
      const response = await getAllEmployeesByIdVendorAndIdCompany(selectedCompany.idCompany, userProfile && userProfile.idVendor);

      if (response && response.data) {
        setIsFetching(false);
        setEmployeeList(response.data);
        setFilteredEmployees(response.data);
      } else {
        setIsFetching(false);
      }
    } catch (error) {
      setIsFetching(false);
      console.error(error);
    }
  }

  useEffect(() => {
    if (selectedCompany && selectedCompany.idCompany) {
      fetchedAllEmployees();
    }
  }, [selectedCompany]);

  const handleOpenAlert = () => {
    setOpenAlert(true);
    setIsFetching(false);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleChangeAlertType = (newValue) => {
    setAlertType(newValue);
  };

  const onChangeCompany = (newValue) => {
    setSelectedCompany(newValue);
  };

  useEffect(() => {
    dispatch(getAllEmployees());
  }, []);

  const fetchedDataToExport = async () => {
    try {
      const response = await getEmployeesPFvalue({
        selectedYear: dayjs(inputSearch).locale('th').format("YYYY"),
        selectedMonth: dayjs(inputSearch).locale('th').format("MM"),
        type: "PVF",
        idCompany: selectedCompany && selectedCompany.idCompany
      });

      if (response && response.data && response.status === 200) {
        setExportData(response.data);
      } else {
        setExportData(null);
      }
    } catch (error) {
      setNoData(true);
      console.error(error);
    }
  };

  const fetchedData = async () => {
    try {
      setIsFetching(true);
      const response = await getPFvalueAllYearByEmpReport({
        year: dayjs(inputSearch).format("YYYY"),
        idCompany: selectedCompany && selectedCompany.idCompany
      });

      if (response && response.data && response.data.employeeData) {
        setIsFetching(false);
        setChartData(response.data.monthSummary);
        setProvidentData(response.data.employeeData);
        setJanSum(response.data.JanSummary);
      } else {
        handleChangeAlertType(`${t("NoData")}`);
        handleOpenAlert(true);
        setIsFetching(false);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (inputSearch && selectedCompany && selectedCompany.idCompany) {
      fetchedData();
    } else {
      setIsFetching(false);
    }
  }, [inputSearch, selectedCompany]);

  useEffect(() => {
    if (inputSearch && selectedCompany && selectedCompany.idCompany) {
      fetchedDataToExport();
    }
  }, [inputSearch, selectedCompany]);

  useEffect(() => {
    if (companyLists && companyLists.length > 0) {
      setSelectedCompany(companyLists[0])
    }
  }, [companyLists]);

  const checkedYear = parseInt(dayjs(inputSearch).locale('th').format("YYYY"));

  const getMonthName = (monthNumber) => {
    const monthNames = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return monthNames[monthNumber - 1];
  };

  const handleSearchClick = (e) => {
    const searchTerm = e.target.value;
    setSearchName(searchTerm);

    const filtered = employeeList && employeeList.length > 0 && employeeList.filter((item) => {
      const firstName = item.firstname;
      const lastName = item.lastname;
      const fullName = firstName + " " + lastName;

      return (searchTerm ? (firstName.includes(searchTerm) || lastName.includes(searchTerm) || fullName === searchTerm) : true);
    });
    setFilteredEmployees(filtered);

    const filteredPFdata = exportData && exportData.length > 0 && exportData.filter(e => {
      const firstName = e.firstname;
      const lastName = e.lastname;
      const fullName = firstName + " " + lastName;

      return (firstName.includes(searchTerm) || lastName.includes(searchTerm) || fullName === searchTerm);
    });
    setFilteredDataXlsx(filteredPFdata);
  };

  const handleClearSearchName = () => {
    setSearchName("");
  };

  const DownloadFile = async (type, selectedYear, selectedMonth, fileType) => {
    if (fileType === "PDF") {
      PfPDFfile(type, selectedYear, selectedMonth, filteredDataXlsx, selectedCompany);
    } else if (fileType === "XLSX") {
      PVFxlsxFile(filteredDataXlsx);
    }
  };

  return (
    <div>
      <StyledRoot className="page">
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              style={{ textDecoration: "none", color: "inherit" }}
              to={"/report-center"}
            >
              {`${t("AllReports")}`}
            </Link>
            <Typography color="text.primary">
              Dashboard Provident Fund
            </Typography>
          </Breadcrumbs>
          <div style={{ marginTop: "5vh" }}>
            <Typography variant="h4" style={{ paddingTop: 8 }}>
              Dashboard Provident Fund
            </Typography>
          </div>

          <StyledWrapFilter>
            <Grid
              container
              spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}
              alignItems={"center"}
            >
              <Grid item xs={2}>
                <StyledBoxSearch>
                  <Typography className="label" color="text.third">
                    {t("SelectYear")}
                  </Typography>
                  <div className="search-date">
                    <DatePickerCustom
                      maxDate={dayjs()}
                      inputFormat="YYYY"
                      value={inputSearch}
                      name="start"
                      views={['year']}
                      openTo={'year'}
                      onChange={(newValue) => {
                        setInputSearch(newValue);
                      }}
                      disabled={isFetching}
                    />
                  </div>
                </StyledBoxSearch>
              </Grid>

              <Grid item xs={2}>
                <StyledBoxSearch>
                  <Typography className="label" color="text.third">
                    {t("SelectMonth")}
                  </Typography>
                  <div className="search-date">
                    <DatePickerCustom
                      maxDate={dayjs()}
                      inputFormat="MMMM"
                      value={inputSearch}
                      name="start"
                      views={['month']}
                      openTo={'month'}
                      onChange={(newValue) => {
                        setInputSearch(newValue);
                      }}
                      disabled={isFetching}
                    />
                  </div>
                </StyledBoxSearch>
              </Grid>

              <Grid item xs={4}>
                <StyledBoxSearch>
                  <Typography className="label" color="text.third">
                    {t("Company")}
                  </Typography>
                  <div className="search-name">
                    <SelectAllCompaniesVendor
                      options={companyLists ? companyLists : null}
                      value={selectedCompany}
                      disabled={isFetching}
                      onChange={(_, value) => {
                        onChangeCompany(value);
                      }}
                    />
                  </div>
                </StyledBoxSearch>
              </Grid>

              <Grid item xs={2}>
                <StyledBoxSearchButton>
                  <div style={{ marginTop: 20 }}>
                    <ButtonBlue
                      variant="contained"
                      startIcon={<DownloadRoundedIcon />}
                      disabled={!exportData}
                      onClick={() => DownloadFile("PVF", parseInt(dayjs(inputSearch).locale('th').format("YYYY")) + 543, dayjs(inputSearch).format("MM"), "PDF")}
                      sx={{ width: 160 }}
                    >
                      {t("Download")} PDF
                    </ButtonBlue>
                  </div>
                </StyledBoxSearchButton>
              </Grid>

              <Grid item xs={2}>
                <StyledBoxSearchButton>
                  <div style={{ marginTop: 20 }}>
                    <ButtonBlue
                      variant="contained"
                      startIcon={<DownloadRoundedIcon />}
                      disabled={!exportData}
                      onClick={() => DownloadFile("PVF", parseInt(dayjs(inputSearch).locale('th').format("YYYY")) + 543, dayjs(inputSearch).format("MM"), "XLSX")}
                      sx={{ width: 160 }}
                    >
                      {t("Download")} XLSX
                    </ButtonBlue>
                  </div>
                </StyledBoxSearchButton>
              </Grid>
            </Grid>
          </StyledWrapFilter>

          <CardStyle style={{ marginTop: "5vh" }}>
            <StyledDetailCard>

              <Stack
                spacing={2}
                direction={"row"}
                alignItems={"center"}
              >
                <TextFieldTheme
                  variant={"outlined"}
                  label={t("searchFirstLast")}
                  disabled={isFetching}
                  value={searchName || ""}  // Bind the value to searchName state
                  onChange={handleSearchClick}  // Use the merged function
                />

                {searchName && (
                  <ButtonBlue
                    variant={"contained"}
                    onClick={() => handleClearSearchName()}
                    color="error"
                    sx={{ width: 150 }}
                  >
                    {t("ClearData")}
                  </ButtonBlue>
                )}
              </Stack>

              <TableContainer component={Paper} style={{ marginTop: "3vh" }}>
                <DivEmployeesList>
                  {employeeList && (
                    <Fragment>
                      <div className="employee-list">
                        <div className="employee-item">
                          <div
                            style={{
                              height: "104px",
                              minHeight: "104px",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography color="text.secondary">
                                Employee<span>{`(${employeeList.length} People)`}</span>
                              </Typography>
                            </div>
                          </div>

                          <div className="employee-item" style={{ marginTop: 32 }}>
                            {ProvidentFundItems.map((item, index) => (
                              <div key={index} className="div-text-end">
                                <Typography variant="body2" align="center">
                                  {item.text}
                                </Typography>
                              </div>
                            ))}
                          </div>
                        </div>

                        {filteredEmployees && filteredEmployees.map((emp, index) => {
                          const findEmp = providentData && providentData[emp.idEmp];
                          const checkedYear = findEmp && findEmp.length > 0 ? findEmp[0].year : "-";

                          return (
                            <div className="employee-item" key={index + emp.firstname + emp.lastname}>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Avatar
                                  className="MuiAvatar-root"
                                  key={index}
                                  alt={emp.email}
                                  src={emp.imageProfile}
                                />
                                <Typography>{`${emp.firstname} ${emp.lastname}`}</Typography>
                              </div>

                              <div style={{ marginTop: 12 }}>
                                <Typography
                                  variant="body2"
                                  align="center"
                                >
                                  พนักงาน/บริษัท
                                </Typography>
                              </div>

                              <div className="employee-item">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                                  <div key={month} className="div-text-end">
                                    <Typography variant="body2" align="center">
                                      {month === 1 && checkedYear < 2024 && findEmp && findEmp.length > 0 ?
                                        findEmp[0].Jan_Provident : (findEmp && findEmp.find(data => data.month === month) ? findEmp.find(data => data.month === month).Total_Provident.toFixed(2) : 0.00)}
                                      /{month === 1 && checkedYear < 2024 && findEmp && findEmp.length > 0 ?
                                        findEmp[0].Jan_Provident : (findEmp && findEmp.find(data => data.month === month) ? findEmp.find(data => data.month === month).Total_Com_Provident.toFixed(2) : 0.00)}
                                    </Typography>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                      </div>
                    </Fragment>
                  )}
                </DivEmployeesList>
              </TableContainer>
            </StyledDetailCard>
          </CardStyle>

          <CardStyle style={{ marginTop: "5vh" }}>
            <StyledDetailCard>
              <ChartProvidentFund Provident={chartData} year={checkedYear} JanValue={JanSum} />
            </StyledDetailCard>
          </CardStyle>

          <CardStyle style={{ marginTop: "5vh" }}>
            <StyledDetailCard style={{ height: "100%" }}>
              <Typography color="text.primary">Total Provident Fund</Typography>
              <Grid item xs={12} style={{ marginTop: "3vh" }}>
                <Grid container spacing={2}>
                  {checkedYear < 2024 &&
                    <Grid item xs={12} md={6} lg={3}>
                      <CardData
                        title="มกราคม"
                        dataValue={JanSum > 0 ? JanSum : 0.00}
                        dataUnit="บาท"
                        dynamicFontSize
                        titleIcon={
                          <CalendarMonthIcon
                            style={{
                              display: "inline-table",
                              color: "#008FFB",
                            }}
                          />
                        }
                      />
                    </Grid>
                  }

                  {Object.keys(chartData).map(month => (
                    <Grid item xs={12} md={6} lg={3} key={month}>
                      <CardData
                        title={getMonthName(Number(month))}
                        dataValue={chartData[month].Total_Provident}
                        dataUnit="บาท"
                        dynamicFontSize
                        titleIcon={
                          <CalendarMonthIcon
                            style={{
                              display: "inline-table",
                              color: "#008FFB",
                            }}
                          />
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </StyledDetailCard>
          </CardStyle>

          <CardStyle style={{ marginTop: "5vh" }}>
            <StyledDetailCard style={{ height: "100%" }}>
              <Typography color="text.primary">Total Company Provident Fund</Typography>
              <Grid item xs={12} style={{ marginTop: "3vh" }}>
                <Grid container spacing={2}>
                  {checkedYear < 2024 &&
                    <Grid item xs={12} md={6} lg={3}>
                      <CardData
                        title="มกราคม"
                        dataValue="0"
                        dataUnit="บาท"
                        dynamicFontSize
                        titleIcon={
                          <CalendarMonthIcon
                            style={{
                              display: "inline-table",
                              color: "#008FFB",
                            }}
                          />
                        }
                      />
                    </Grid>
                  }

                  {Object.keys(chartData).map(month => (
                    <Grid item xs={12} md={6} lg={3} key={month}>
                      <CardData
                        title={getMonthName(Number(month))}
                        dataValue={chartData[month].Total_Com_Provident.toFixed(2)}
                        dataUnit="บาท"
                        dynamicFontSize
                        titleIcon={
                          <CalendarMonthIcon
                            style={{
                              display: "inline-table",
                              color: "#008FFB",
                            }}
                          />
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </StyledDetailCard>
          </CardStyle>
        </Container>
        <AlertResponse
          open={openAlert}
          handleClose={handleCloseAlert}
          alertType={alertType}
        />
      </StyledRoot>
    </div>
  );
};

export default DashboardProvidentFund;